/** EvalTestSetWidget — author optimization and holdout cases (Experiment tab).
 *
 * Test cases sync to a Langfuse dataset server-side; this is just the editor.
 * Small set → simple add/edit/delete with refetch (see useTestCases).
 */

import { useState } from 'react';
import { toast } from '@/shared/ui/sonner';

import type { EvalTestCase, EvalTestCaseInput } from '@/entities/eval';
import { log } from '@/shared/logger';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import type { UseTestCasesResult } from '../../../features/settings/evaluation/useTestCases';

interface EditorProps {
  initial?: EvalTestCase;
  onSave: (input: EvalTestCaseInput) => Promise<void>;
  onCancel: () => void;
}

function TestCaseEditor({ initial, onSave, onCancel }: EditorProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [input, setInput] = useState(initial?.input ?? '');
  const [expected, setExpected] = useState(initial?.expected ?? '');
  const [split, setSplit] = useState<EvalTestCaseInput['split']>(initial?.split ?? 'optimization');
  const [saving, setSaving] = useState(false);

  const canSave = name.trim().length > 0 && input.trim().length > 0 && !saving;

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        input: input.trim(),
        expected: expected.trim() || null,
        split,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3">
      <div className="flex flex-col gap-1">
        <Label htmlFor="tc-name">Name</Label>
        <Input id="tc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Refund policy question" />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="tc-split">Evaluation split</Label>
        <select
          id="tc-split"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
          value={split}
          onChange={(event) => setSplit(event.target.value as EvalTestCaseInput['split'])}
        >
          <option value="optimization">Optimization — may guide prompt suggestions</option>
          <option value="holdout">Holdout — used only for the final comparison</option>
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="tc-input">Input (user message)</Label>
        <Textarea id="tc-input" value={input} onChange={(e) => setInput(e.target.value)} rows={3} />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="tc-expected">Expected (optional reference for the evaluator)</Label>
        <Textarea id="tc-expected" value={expected} onChange={(e) => setExpected(e.target.value)} rows={2} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={() => void save()} disabled={!canSave}>
          Save
        </Button>
      </div>
    </div>
  );
}

export default function EvalTestSetWidget({ state }: { state: UseTestCasesResult }) {
  const { testCases, loading, error, create, update, remove } = state;
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);

  const handleSave = async (id: string | 'new', input: EvalTestCaseInput) => {
    try {
      if (id === 'new') await create(input);
      else await update(id, input);
      setEditingId(null);
      toast.success('Test case saved');
    } catch (err) {
      log.error('eval_test_case_save_failed', err);
      toast.error('Could not save test case');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await remove(id);
      toast.info('Test case removed');
    } catch (err) {
      log.error('eval_test_case_delete_failed', err);
      toast.error('Could not remove test case');
    }
  };

  return (
    <section className="flex flex-col gap-3" aria-label="Evaluation cases">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Evaluation cases</h3>
        {editingId === null && (
          <Button
            variant="secondary"
            onClick={() => setEditingId('new')}
            disabled={testCases.length >= 20}
          >
            Add test case
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Optimization cases can guide a rewrite. Holdout cases stay out of the improver and decide whether the
        candidate actually won.
      </p>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading && <p className="text-sm text-muted-foreground">Loading test cases…</p>}

      {editingId === 'new' && (
        <TestCaseEditor onSave={(input) => handleSave('new', input)} onCancel={() => setEditingId(null)} />
      )}

      {!loading && testCases.length === 0 && editingId === null && (
        <p className="text-sm text-muted-foreground">No test cases yet. Add one to begin.</p>
      )}

      <ul className="flex flex-col gap-2">
        {testCases.map((tc) =>
          editingId === tc.id ? (
            <li key={tc.id}>
              <TestCaseEditor
                initial={tc}
                onSave={(input) => handleSave(tc.id, input)}
                onCancel={() => setEditingId(null)}
              />
            </li>
          ) : (
            <li
              key={tc.id}
              className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-foreground">{tc.name}</p>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {tc.split}
                  </span>
                </div>
                <p className="truncate text-sm text-muted-foreground">{tc.input}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button variant="ghost" onClick={() => setEditingId(tc.id)} disabled={editingId !== null}>
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => void handleDelete(tc.id)}
                  disabled={editingId !== null}
                  aria-label={`Delete test case ${tc.name}`}
                >
                  Delete
                </Button>
              </div>
            </li>
          ),
        )}
      </ul>
    </section>
  );
}
